import { StoreInterfaces, StoreTypeMapValues, StoreTypeMap } from '@/classes'
import WindowStore from '@/store/window'

export const useStore = <T extends keyof StoreInterfaces>(
  type: T,
  options?: {
    indexes?: string[]
    expiration?: number // in minutes
  }
) => {
  const indexKeys = options?.indexes ?? []
  const indexes: Ref<Map<string, Map<string, string[]>>> = ref(
    new Map(indexKeys.map(k => [ k, new Map() ]))
  )


  const data: Ref<Map<string, { lastFetch: Date, priority: number, data: StoreInterfaces[T] }>> = ref(new Map())

  const lastAccess = ref(new Map<string, Date>())

  const getItem = (
    value?: string,
    index: string = '_id'
  ) => {
    if (!value) return null

    const ids = index === '_id' ? [ value ] : (indexes.value.get(index)?.get(value) ?? [])
    const results: StoreTypeMap[T][] = ids.map(i => data.value.get(i)).filter(i => i?.data).map(item => new (StoreTypeMapValues[type] as any)((item as any).data))

    results.forEach(i => lastAccess.value.set(i._id, new Date()))

    return results[0] ?? null
  }

  const getItemMulti = (
    value?: string,
    index: string = '_id'
  ) => {
    if (!value) return []

    const ids = index === '_id' ? [ value ] : (indexes.value.get(index)?.get(value) ?? [])
    const results: StoreTypeMap[T][] = ids.map(i => data.value.get(i)).filter(i => i?.data).map(item => new (StoreTypeMapValues[type] as any)((item as any).data))

    results.forEach(i => lastAccess.value.set(i._id, new Date()))

    return results
  }
  
  const getItems = (
    _ids?: string[],
    index: string = '_id'
  ) => {
    const ids = (_ids ?? []).reduce((all: string[], value) => [
      ...all,
      ...(index === '_id' ? [ value ] : [ ...(indexes.value.get(index)?.get(value) ?? []) ])
    ], [])
    
    const results: StoreTypeMap[T][] = ids.map(i => data.value.get(i)).filter(i => i?.data).map(item => new (StoreTypeMapValues[type] as any)((item as any).data))

    results.forEach(i => lastAccess.value.set(i._id, new Date()))

    return results
  }

  const cleanup = () => {
    if (!options?.expiration || data.value.size < 100) return true

    const expired = Array.from(lastAccess.value.entries()).filter(([_, date]) => {
      const diff = new Date().getTime() - date.getTime()
      return diff > (options.expiration ?? 9999) * 60 * 1000
    })

    expired.forEach(([key]) => {
      deleteItem(key)
    })

    return true
  }

  const setItem = (
    item: StoreInterfaces[T],
    options?: {
      priority?: number
    }
  ) => {
    const existing = data.value.get((item as any)._id)
    const populatedKeys = existing?.data ? Object.keys(existing?.data).filter(k => k.includes('@')) : []

    const isInferior = existing && (options?.priority ?? 0) < existing.priority

    data.value.set((item as any)._id, {
      lastFetch: new Date(),
      priority: isInferior ? existing.priority : options?.priority ?? 0,
      data: isInferior ? existing.data : {
        ...item,
        ...populatedKeys.reduce((acc, key) => {
          return { ...acc, [key]: item[key as keyof typeof item] ?? existing?.data?.[key as keyof typeof existing.data] }
        }, {})
      }
    })

    if (item) lastAccess.value.set((item as any)._id, new Date())

    indexKeys.forEach(index => {
      const selectedIndex = indexes.value.get(index)
      if (selectedIndex) selectedIndex.set((item as any)[index], [
        ...new Set([
          ...(selectedIndex.get((item as any)[index]) ?? []), item._id
        ])
      ])
    })
  }

  const updateItem = (
    _id: string,
    params: Partial<StoreInterfaces[T]>,
    index: string = '_id'
  ) => {
    const existing = data.value.get(_id)
    if (!existing) return false

    data.value.set(_id, {
      ...existing,
      data: {
        ...existing.data,
        ...params
      }
    })

    return true
  }

  const deleteItem = (value: string) => {
    const result = data.value.get(value)?.data

    indexKeys.forEach(index => {
      const selectedIndex = indexes.value.get(index)

      if (selectedIndex && result) {
        const filtered = (selectedIndex.get((result as any)[index]) ?? []).filter(i => i !== value)

        if (filtered.length === 0) {
          selectedIndex.delete((result as any)[index])
        } else {
          selectedIndex.set((result as any)[index], filtered)
        }
      }
    })

    lastAccess.value.delete(value)

    data.value.delete(value)
  }

  const windowStore = WindowStore()
  const { debug } = storeToRefs(windowStore)

  watch(data, () => {
    debug.value.set(`store/${type}`, data.value.size)
    cleanup()
  }, { deep: true })
  
  return {
    getItem, getItemMulti, getItems, setItem, deleteItem, updateItem
  }
}